/**
 * Áreas dinámicas — la clínica las administra desde Configuración › Áreas.
 * Funciona en modo API (Postgres/Oracle) y en modo Firebase. Si la colección
 * está vacía, las pantallas caen al listado estático de utils/areas.ts.
 */
import {
  collection,
  doc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';

export interface Area {
  slug: string;
  nombre: string;
  descripcion?: string | null;
  activa: boolean;
  pedible: boolean;
  orden?: number;
}

export const areasQuery: any = USE_API
  ? apiQuery('/areas', { _order: 'orden' })
  : query(collection(firestore, 'areas'), orderBy('orden'));

/** Convierte "Sala de Rayos X" → "sala_de_rayos_x". */
export function slugifyArea(nombre: string): string {
  const base = nombre
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // saca tildes
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || `area_${Date.now()}`;
}

export interface CrearAreaInput {
  nombre: string;
  descripcion?: string;
  pedible?: boolean;
}

export async function crearArea(input: CrearAreaInput): Promise<string> {
  const slug = slugifyArea(input.nombre);
  const payload = {
    slug,
    nombre: input.nombre.trim(),
    descripcion: input.descripcion?.trim() || null,
    activa: true,
    pedible: input.pedible ?? true,
    orden: 100,
  };
  if (USE_API) {
    await api.post('/areas', payload);
    return slug;
  }
  await setDoc(doc(collection(firestore, 'areas'), slug), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return slug;
}

export interface ActualizarAreaInput {
  nombre?: string;
  descripcion?: string | null;
  activa?: boolean;
  pedible?: boolean;
  orden?: number;
}

export async function actualizarArea(slug: string, patch: ActualizarAreaInput): Promise<void> {
  if (USE_API) {
    await api.patch(`/areas/${slug}`, { ...patch, updatedAt: new Date().toISOString() });
    return;
  }
  await updateDoc(doc(collection(firestore, 'areas'), slug), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function eliminarArea(slug: string): Promise<void> {
  if (USE_API) {
    await api.delete(`/areas/${slug}`);
    return;
  }
  await updateDoc(doc(collection(firestore, 'areas'), slug), {
    activa: false,
    updatedAt: serverTimestamp(),
  });
}
